import AuthActions from 'actions/AuthActions';
import ContextActions from 'actions/ContextActions';
import { clearCurrentScrollEvent } from 'actions/currentScrollEventActions';
import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'stores/store';
import FeaturesUtil from 'utils/FeaturesUtil';
import { getQueryProps } from 'utils/RouteUtil';
import ErrorBoundary from '../core/components/ErrorBoundary';

export interface PrivateRouteProps {
  /**
   * If present, when you visit a private route this will fire a `ContextActions.visitPage()`
   * action to switch to the provided page.
   */
  contextAction?: string;
  children: React.ReactNode;
}

/**
 * PrivateRoute is a Route where the user needs to be logged in to access the
 * route.  Visiting this route will also dispatch the `contextAction`
 * provided in the `props` whenever this route is navigated to.
 */
const PrivateRoute: React.FC<PrivateRouteProps> = (props) => {
  const { children, contextAction } = props;
  const location = useLocation();
  const dispatch = useDispatch();

  const state = location?.state;

  useEffect(() => {
    if (contextAction) {
      // Clear any scollable event when switching pages.
      dispatch(clearCurrentScrollEvent());

      // Because private routes can often be rendered because of another flux
      // event being dispatched, we fire this event asynchronously.
      setTimeout(
        () =>
          ContextActions.visitPage({
            context: contextAction,
            state,
          }),
        0,
      );
    }
  }, [dispatch, contextAction, state]);

  const canUseWeb = FeaturesUtil.canAccessWeb();

  // Store the "original location" for initializing event filters
  const originalLocation = useRef(location);
  useEffect(() => {
    setTimeout(() => {
      const queryProps = getQueryProps({ location: originalLocation.current });
      AuthActions.storeQuery(queryProps);
    }, 0);
  }, []);

  if (!canUseWeb) {
    return <Navigate to="/web-access-restricted" />;
  }
  return <ErrorBoundary>{children}</ErrorBoundary>;
};

export default PrivateRoute;
